import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { areasAPI } from '../utils/api';
import { useAuth } from './AuthContext';

export interface SidebarArea {
  id: number;
  name: string;
  cameras_count?: number;
  cameras_online?: number;
}

interface AreasContextType {
  areas: SidebarArea[];
  loading: boolean;
  /** Перезагрузить список участков (например, после создания/удаления). */
  refreshAreas: () => Promise<void>;
}

const AreasContext = createContext<AreasContextType | undefined>(undefined);

/**
 * Список участков для навигации.
 *
 * Отдельный лёгкий контекст нужен боковому меню, чтобы показывать
 * вложенные разделы. Страницы продолжают работать со своими данными —
 * контекст ничего не подменяет и лишь дублирует чтение списка.
 */
export const useAreasNav = (): AreasContextType => {
  const context = useContext(AreasContext);
  if (!context) {
    return { areas: [], loading: false, refreshAreas: async () => {} };
  }
  return context;
};

interface AreasProviderProps {
  children: ReactNode;
}

export const AreasProvider: React.FC<AreasProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [areas, setAreas] = useState<SidebarArea[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshAreas = useCallback(async () => {
    if (!isAuthenticated) {
      setAreas([]);
      return;
    }
    try {
      setLoading(true);
      const response = await areasAPI.getAll();
      if (response.success) {
        setAreas(response.areas);
      }
    } catch {
      /* навигация не критична: при ошибке просто не показываем вложенный список */
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      void refreshAreas();
    } else {
      setAreas([]);
    }
  }, [isAuthenticated, refreshAreas]);

  const value = useMemo(
    () => ({ areas, loading, refreshAreas }),
    [areas, loading, refreshAreas]
  );

  return <AreasContext.Provider value={value}>{children}</AreasContext.Provider>;
};
